// externals
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, Route, Switch } from 'react-router-dom';
import { OktaAuth, toRelativeUrl, getToken } from '@okta/okta-auth-js';
import { LoginCallback, SecureRoute, Security } from '@okta/okta-react';
import type { TokenParams, PopupParams, OktaAuthOptions } from '@okta/okta-auth-js';
import type { ComponentClass, FC, FunctionComponent, PropsWithChildren } from 'react';

// libraries
import { hostUrlHelper, locHrefUtil, offerSessionUtils } from '@makemydeal/dr-shared-ui-utils';
import { apiConfigConsts } from '@makemydeal/dr-shared-ui-utils';
import { registerTokenRenewalCallback, setOktaAuthForInterceptors } from '@makemydeal/dr-shared-network';

// utils
import { isProductionEnvironment } from './oktaConfig';
import type { AuthConfigCallback } from './oktaConfig';

// constants
import { OKTA_OAUTH_CALLBACK } from '../constants';

// components
import { Logout } from './Logout';

export const ADDITIONAL_QUERY_PARAMS_WHITE_LIST = [
    apiConfigConsts.BFF_ENV,
    apiConfigConsts.UI_ENV,
    apiConfigConsts.THEME,
    // TODO: SV - add this back later
    // apiConfigConsts.LOAD_AURYC,
    apiConfigConsts.ENABLE_TOGGLES,
    apiConfigConsts.ENABLE_DEALER_TOGGLES,
    apiConfigConsts.DISABLE_DEALER_TOGGLES,
    apiConfigConsts.DISABLE_TOGGLES,
    apiConfigConsts.DASH_CSS,
    apiConfigConsts.TARGET,
    apiConfigConsts.DEAL_TEMPLATES_VIEW,
    apiConfigConsts.REDUX_STORE_MODE,
    apiConfigConsts.SHOW_DEAL_MESSAGES,
    apiConfigConsts.USE_CRM_EMBEDDED_MODE,
    // PR env params: persisted so pr-index.html can recover the PR number after Okta auth redirect
    apiConfigConsts.PR_NUMBER_SESSION_STORAGE_NAME,
    apiConfigConsts.PR_ENV_SESSION_STORAGE_NAME
];

export type OfferReadyContentProps = PropsWithChildren<{
    identified: boolean;
    renewTokens?: () => Promise<boolean>;
}>;

export type AuthManagerProps = PropsWithChildren<{
    appPrefix: string;
    getConfig: AuthConfigCallback;
    offerReadyContent: FunctionComponent<OfferReadyContentProps> | ComponentClass<OfferReadyContentProps> | string;
    byPassOkta?: boolean;
    onSignOut?: () => void;
    onTokenRenewalFailure?: () => boolean;
}>;

export const handleTokenRenewalFailure = async (
    auth: Pick<OktaAuth, 'signInWithRedirect'>,
    onTokenRenewalFailure?: () => boolean
): Promise<false> => {
    if (onTokenRenewalFailure?.()) {
        return false;
    }
    await auth.signInWithRedirect();
    return false;
};

/**
 * Manages initialization of Okta and the PCKE oauth workflow before loading the rest of the application
 */
export const AuthManager: FC<AuthManagerProps> = ({
    appPrefix,
    children,
    getConfig,
    offerReadyContent: OfferReadyContent,
    byPassOkta,
    onSignOut,
    onTokenRenewalFailure
}) => {
    const onTokenRenewalFailureRef = useRef(onTokenRenewalFailure);
    onTokenRenewalFailureRef.current = onTokenRenewalFailure;
    const oktaDisabled = useMemo(() => process.env.BYPASS_OKTA === 'true' || byPassOkta, [byPassOkta]);
    const [offerIdentified, setOfferIdentified] = useState(false);
    const history = useHistory();
    const location = useLocation();

    const environment = hostUrlHelper.getHostUrlEnvironment(appPrefix, locHrefUtil.getLocHref());

    // Setup the OktaAuth engine with config
    const config = { ...getConfig(isProductionEnvironment(environment)) };
    const { oktaAuth, renewTokens } = useMemo(() => {
        if (oktaDisabled) {
            return {
                oktaAuth: undefined,
                renewTokens: undefined
            };
        }
        const authOptions: OktaAuthOptions = {
            issuer: config.issuer,
            clientId: config.clientId,
            redirectUri: `${window.location.origin}${OKTA_OAUTH_CALLBACK}`,
            pkce: true,
            scopes: config.scopes,
            tokenManager: {
                expireEarlySeconds: 60,
                // setting autoRenew AND autoremove to false allows us to silently renew tokens when they expire instead of a page refresh
                autoRenew: false,
                autoRemove: false
            },
            storageManager: {
                token: {
                    storageKey: `okta_token_${config.id}`,
                    storageTypes: ['localStorage', 'sessionStorage', 'cookie']
                },
                cache: {
                    storageKey: `okta_cache_${config.id}`,
                    storageTypes: ['localStorage', 'sessionStorage', 'cookie']
                },
                transaction: {
                    storageKey: `okta_transaction_${config.id}`,
                    storageTypes: ['sessionStorage', 'localStorage', 'cookie']
                },
                'shared-transaction': {
                    storageKey: `okta_shared_transaction_${config.id}`,
                    storageTypes: ['localStorage']
                },
                'original-uri': {
                    storageKey: `okta_original_uri_${config.id}`,
                    storageTypes: ['localStorage']
                }
            }
        };
        // istanbul ignore next
        const auth = new OktaAuth(authOptions);
        setOktaAuthForInterceptors(auth);
        // istanbul ignore next
        async function renewTokens(): Promise<boolean> {
            const options: TokenParams & PopupParams = {
                responseMode: 'okta_post_message',
                scopes: auth.options.scopes,
                timeout: 10000,
                display: null as any // required for renew.
            };

            try {
                const response = await getToken(auth, options);

                auth.tokenManager.setTokens(response.tokens);
                await auth.authStateManager.updateAuthState();
                return true;
            } catch (err: any) {
                return handleTokenRenewalFailure(auth, onTokenRenewalFailureRef.current);
            }
        }
        // istanbul ignore next
        auth.tokenManager.on('expired', renewTokens);

        // Register the token renewal callback for use by network layer
        // istanbul ignore next
        registerTokenRenewalCallback(renewTokens);

        // istanbul ignore next
        return { oktaAuth: auth, renewTokens };
    }, []);

    // istanbul ignore next
    const restoreOriginalUri = async (_okta: OktaAuth, originalUri: string) => {
        // istanbul ignore next
        const originalUriResolved = originalUri || '/';
        const originalUrl = `${window.location.origin}${originalUriResolved}`;
        offerSessionUtils.parseAndWriteAdditionalToggles(window.location.href, { whitelist: ADDITIONAL_QUERY_PARAMS_WHITE_LIST });
        offerSessionUtils.parseAndWriteOfferIdentifiers(originalUrl);
        setOfferIdentified(true);
        history.replace(toRelativeUrl(originalUriResolved, window.location.origin));
    };

    useEffect((): void => {
        offerSessionUtils.parseAndWriteOfferIdentifiers(window.location.href); // full href
        offerSessionUtils.parseAndWriteAdditionalToggles(window.location.href, { whitelist: ADDITIONAL_QUERY_PARAMS_WHITE_LIST });

        setOfferIdentified(true);
    }, [location]);

    // istanbul ignore next
    const onOktaSignOut = useCallback(() => {
        onSignOut?.();
        void oktaAuth?.signOut().catch((error) => {
            console.warn('Error during Okta sign out:', error);
        });
    }, [oktaAuth, onSignOut]);

    // if Okta instance isn't available, just return a blank screen when the app is not ready; otherwise, show children without secure routes
    if (oktaAuth == null) {
        return <OfferReadyContent identified={offerIdentified}>{children}</OfferReadyContent>;
    }

    return (
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
            <Switch>
                <Route path={OKTA_OAUTH_CALLBACK} exact component={LoginCallback} />
                <Route path="/logout" exact>
                    <Logout handleLogout={onOktaSignOut} />
                </Route>
                <SecureRoute path="*">
                    <OfferReadyContent identified={offerIdentified} renewTokens={renewTokens}>
                        {children}
                    </OfferReadyContent>
                </SecureRoute>
            </Switch>
        </Security>
    );
};
